import React, { useContext, useEffect, useState, useMemo } from "react";
import cloneDeep from "lodash/cloneDeep";
import styled, { ThemeContext } from "styled-components";
import { LoadingData } from "./_helpers/LoadingData";
import { useParams } from "react-router-dom";
import { MachineMetadataFC } from "../_helpers/displayFiles/MachineMetadataFC";
import { useAuth0 } from "@auth0/auth0-react";
import { _ChartIndex } from "../_helpers/charts/_ChartIndex";
import { _FusionGridIndex } from "../_helpers/fusionCharts/_FusionGridIndex";
import axiosInstance from "../../../../appHelpers/axiosInstance";
import { renderCount } from "../../../../appHelpers/appLoggers";
import { Loader_MachineMetadataFC } from "../_helpers/displayFiles/loaders/Loader_MachineMetadataFC";
import { FlexboxGrid, Message } from "rsuite";
import { EventHistoryFC } from "../_helpers/displayFiles/EventHistoryFC";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { InputContext } from "../_context/InputContext";
import { getMessage } from "../_utils/util_general";
import { UserContext } from "../_context/UserContext";
import { LogTableFC } from "./_helpers/LogTableFC";
import { LogAlertTableFC } from "./_helpers/LogAlertTableFC";
import { MainDivider } from "../admin/helpers/Admin_StyledComponents";
import { DateTime } from "luxon";
import { FileHeader } from "./FileHeader";
import { SegmentedPicker, Button, GlassCard, colors } from "../../../ui";

export const _HHMSystemFC = () => {
  renderCount("_MachineFC");
  const { getAccessTokenSilently } = useAuth0();
  const [selectedDataView, setSelectedDataView] = useState(`TABLE`);
  const dataViewOptions = [
    { eventKey: "TABLE", label: "Data Table" },
    { eventKey: "ALERTS", label: "Log Alerts" },
  ]; //`LOG`, When the time is right :P
  const { getUserProfile, user } = useContext(UserContext);
  // ADDED FIELDS
  const [loadingMetadata, setLoadingMetadata] = useState(true);
  const [loadingDataTable, setLoadingDataTable] = useState(true);
  const [loadingRecentFiles, setLoadingRecentFiles] = useState(true);
  const [metadata, setMetadata] = useState([]);
  const [recentFiles, setRecentFiles] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [dataConfig, setDataConfig] = useState([]);
  const [getMetadataError, setGetMetadataError] = useState(false);
  const [getDataTableError, setGetDataTableError] = useState(false);
  const [getDataConfigError, setGetDataConfigError] = useState(false);
  const [getRecentFilesError, setGetRecentFilesError] = useState(false);
  const [level, setLevel] = useState(1);
  const { id, dataModel } = useParams();
  // NETWORK ISSUES
  const [loadingIssues, setLoadingIssues] = useState(true);
  const [issues, setIssues] = useState([]);

  // LOG ALERTS
  const [logAlerts, setLogAlerts] = useState([]);
  const [loadingLogAlerts, setLoadingLogAlerts] = useState(false);
  const [getLogAlertsError, setGetLogAlertsError] = useState(false);

  const history = useHistory();
  const [activeKey, setActiveKey] = useState("1");

  const { viewToggle, setViewToggle } = useContext(InputContext);

  const getMachineMetadata = async () => {
    // console.log(`getMachineMetadata`);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "get",
        url: `/system/metadata/${id}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setMetadata(response.data[0][0]);
    } catch (error) {
      console.error(error);
      setGetMetadataError(true);
    }
    setLoadingMetadata(false);
  };

  const getMachineDataTable = async () => {
    // console.log(`getMachineDataTable`);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "get",
        url: `/system/data/hhm/${id}/0`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setDataTable(cloneDeep(response.data[0]));
    } catch (error) {
      setGetDataTableError(true);
      console.error(error);
    }
    setLoadingDataTable(false);
  };

  const getLogAlerts = async () => {
    // console.log(`getMachineDataTable`);
    setLoadingLogAlerts(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        method: "get",
        url: `/alert/log/models/${id}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setLogAlerts(res.data[0]);
    } catch (error) {
      setGetLogAlertsError(true);
      console.error(error);
    }
    setLoadingLogAlerts(false);
  };

  const getNetworkIssues = async () => {
    setLoadingIssues(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        url: `/network/issues`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setIssues(res?.data[0]);
    } catch (error) {
      console.error(error);
    }
    setLoadingIssues(false);
  };

  const getRecentFiles = async () => {
    setLoadingRecentFiles(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        url: `/system/recent-files/${id}/`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setRecentFiles(res?.data[0][0]);
    } catch (error) {
      console.error(error);
    }
    setLoadingRecentFiles(false);
  };

  useEffect(() => {
    getMachineMetadata();
    getMachineDataTable();
    getNetworkIssues();
    getRecentFiles();
    getLogAlerts();
    getUserProfile();
    setViewToggle("HHM");
  }, []);

  const memoMetadata = useMemo(
    () => <MachineMetadataFC metadata={metadata} />,
    [metadata],
  );

  const memoRecentFiles = useMemo(
    () => <FileHeader recent_files={recentFiles} />,
    [recentFiles],
  );

  const memoMagnetDataTable = useMemo(
    () => (
      <LogTableFC
        dataTable={dataTable}
        dataConfig={dataConfig}
        filterLevel={level}
        metadata={metadata}
      />
    ),
    [dataTable, dataConfig, level],
  );

  const memoLogAlertsTable = useMemo(
    () => (
      <LogAlertTableFC
        logAlerts={logAlerts}
        dataConfig={dataTable?.dataConfig}
        metadata={metadata}
        refresh={getLogAlerts}
        isHHM={true}
      />
    ),
    [logAlerts, dataTable],
  );

  const ModernBreadcrumb = () => {
    return (
      <BreadcrumbContainer>
        <BreadcrumbSection>
          <Greeting>
            {user.first_name
              ? `GOOD ${getMessage()}, ${user.first_name?.toUpperCase()}!`
              : ""}
          </Greeting>
          <BreadcrumbPath>
            <Button
              variant="secondary"
              size="small"
              onClick={() => history.push(`/`)}
            >
              SYSTEMS
            </Button>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <SystemLabel>{id}</SystemLabel>
          </BreadcrumbPath>
        </BreadcrumbSection>
        <UserSection>
          <ModernAvatar
            onClick={() => history.push(`/admin/user/${user.email_address}`)}
          >
            {(user.first_name ? user.first_name.charAt(0) : "") +
              " " +
              (user.last_name ? user.last_name.charAt(0) : "")}
          </ModernAvatar>
        </UserSection>
      </BreadcrumbContainer>
    );
  };

  // CONTROLLING THE LOADING CONDITIONAL WORKS NICELY HERE
  // OTHERWISE, A CHECK IS NEEDED IN THE COMPONENT ITSELF FOR DESTRUCTORING
  // DATA FROM THE CONTEXT WHICH MAY OR MAY NOT HAVE LOADED YET
  const DataView = () => {
    switch (selectedDataView) {
      case `TABLE`:
        return loadingDataTable ? (
          <LoadingData />
        ) : !getDataTableError && !getDataConfigError ? (
          memoMagnetDataTable
        ) : (
          <>Something Went Wrong</>
        );
      case `ALERTS`:
        return loadingLogAlerts || loadingDataTable || loadingMetadata ? (
          <LoadingData />
        ) : !getLogAlertsError ? (
          memoLogAlertsTable
        ) : (
          <>Something Went Wrong</>
        );
      default:
        return <div>NON-CONFORMANT STATE: RemoteFilesFC:DataView</div>;
    }
  };

  return (
    <SystemContainer>
      {/* Modern Breadcrumb Navigation */}
      <ModernBreadcrumb />

      <ContentWrapper>
        {/* System Metadata */}
        {loadingMetadata ? <Loader_MachineMetadataFC /> : memoMetadata}

        {/* System Issues Warning */}
        {issues?.filter((issue) => issue.id === metadata.id).length > 0 &&
          issues
            ?.filter((issue) => issue.id === metadata.id)
            .map((issue) =>
              !issue.resolved &&
              issue.status === "active" &&
              issue.processor_type === "HHM" ? (
                <WarningCard key={issue.id}>
                  <Message
                    showIcon
                    type="warning"
                    header="Warning"
                    closable
                    style={{ width: "100%" }}
                  >
                    This System is experiencing issues.
                    <MainDivider />
                    <FlexboxGrid>
                      <FlexboxGrid.Item colspan={5}>
                        Reported:{" "}
                        {DateTime.fromISO(issue.inserted_at).toFormat("ff")}
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item colspan={3}>
                        Assigned: {issue.assigned}
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item colspan={3}>
                        Type: {issue.type}
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item colspan={12}>
                        Notes: {issue.notes}
                      </FlexboxGrid.Item>
                    </FlexboxGrid>
                  </Message>
                </WarningCard>
              ) : null,
            )}

        {/* Log Event History Bar */}
        <EventHistoryFC />

        {/* Recent Files */}
        {!loadingRecentFiles && memoRecentFiles}

        {/* Navigation for Data Views */}
        <NavigationContainer>
          <SegmentedPicker
            options={dataViewOptions}
            activeKey={selectedDataView}
            onSelect={setSelectedDataView}
            variant="glass"
            size="medium"
          />
        </NavigationContainer>

        {/* Data View Content */}
        <DataContainer>
          <DataView />
        </DataContainer>
      </ContentWrapper>
    </SystemContainer>
  );
};

// Modern Styled Components
const SystemContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 0 24px 24px 24px;
  color: ${colors.neutral[800]};

  @media screen and (max-width: 768px) {
    padding: 0 16px 24px 16px;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px 0;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const BreadcrumbSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Greeting = styled.div`
  font-size: 0.9em;
  color: ${colors.neutral[600]};
  font-weight: 500;

  @media screen and (max-width: 768px) {
    font-size: 0.8em;
  }
`;

const BreadcrumbPath = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BreadcrumbSeparator = styled.span`
  color: ${colors.neutral[500]};
  font-size: 1.1em;
`;

const SystemLabel = styled.span`
  font-size: 1.1em;
  font-weight: 600;
  color: ${colors.primary.main};
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
`;

const ModernAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${colors.primary.main} 0%,
    ${colors.primary.dark} 100%
  );
  box-shadow: 0 4px 12px rgba(44, 90, 160, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(44, 90, 160, 0.4);
  }

  @media screen and (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 12px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;

  @media screen and (max-width: 768px) {
    gap: 2px;
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0px 0;
`;

const WarningCard = styled.div`
  margin: 16px 0;
`;

const DataContainer = styled.div`
  width: 100%;
  margin-top: 16px;
  overflow-x: auto;
  box-sizing: border-box;
`;
